import { prefersReducedMotion } from '@oo/theme';

const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
// Mit Scroll-Timelines erledigt oo-hero.css die Parallaxe allein.
const hasScrollTimeline = CSS.supports('animation-timeline: view()');
const DECODE_WAIT = 3000;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

class OoHero extends HTMLElement {
  // Zählt jeden Angebotswechsel; nie zurückgesetzt, damit ein alter, noch wartender Wechsel nie als aktuell gilt.
  offerTicket = 0;

  connectedCallback() {
    this.card = this.querySelector('.oo-hero__card');
    this.tilt = this.querySelector('[data-oo-tilt]');
    this.toggle = this.querySelector('[data-oo-snow-toggle]');
    this.toggleLabel = this.toggle?.querySelector('[data-oo-snow-label]');
    this.tiltFrame = 0;
    this.scrollFrame = 0;
    this.visible = false;
    this.scrollParallax = false;

    this.onPointerMove = (event) => this.queueTilt(event);
    this.onPointerLeave = () => this.resetTilt();
    this.onScroll = () => this.queueScroll();
    this.onToggle = () => this.setPaused(!this.paused);
    this.onPreferenceChange = () => {
      this.setup();
      this.updateOfferState();
      this.scheduleOffer();
    };
    // Maus und Tastaturfokus halten den Wechsel getrennt an; er läuft erst weiter, wenn beide die Karte verlassen haben.
    // Nur die Maus hält an: Ein Finger (Scrollen über den Banner) löst pointerenter aus, aber nicht auf jedem Handy pointerleave.
    this.onOfferPointerEnter = (event) => {
      if (event.pointerType === 'mouse') this.holdOffer('pointer', true);
    };
    this.onOfferPointerLeave = () => this.holdOffer('pointer', false);
    this.onOfferFocusIn = () => this.holdOffer('focus', true);
    this.onOfferFocusOut = (event) => {
      if (this.card?.contains(event.relatedTarget)) return;
      this.holdOffer('focus', false);
    };

    // Schnee, Parallaxe und Angebotswechsel ruhen, solange der Hero nicht zu sehen ist.
    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.classList.toggle('is-offscreen', !this.visible);
      this.queueScroll();
      this.scheduleOffer();
    });
    this.observer.observe(this);

    this.toggle?.addEventListener('click', this.onToggle);
    finePointer.addEventListener('change', this.onPreferenceChange);
    reducedMotion.addEventListener('change', this.onPreferenceChange);
    this.setupOffers();
    this.setup();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.toggle?.removeEventListener('click', this.onToggle);
    finePointer.removeEventListener('change', this.onPreferenceChange);
    reducedMotion.removeEventListener('change', this.onPreferenceChange);
    this.card?.removeEventListener('pointerenter', this.onOfferPointerEnter);
    this.card?.removeEventListener('pointerleave', this.onOfferPointerLeave);
    this.card?.removeEventListener('focusin', this.onOfferFocusIn);
    this.card?.removeEventListener('focusout', this.onOfferFocusOut);
    // Ein Wechsel, der noch auf sein Foto wartet, wird verworfen.
    this.offerTicket++;
    clearTimeout(this.offerTimer);
    clearTimeout(this.leaveTimer);
    this.teardown();
  }

  // Wechselnde Angebote: bei jedem Besuch ein zufälliges Produkt, danach reihum.
  setupOffers() {
    const box = this.querySelector('[data-oo-offers]');
    this.slides = box ? [...box.querySelectorAll('[data-oo-offer]')] : [];
    this.chips = [...this.querySelectorAll('[data-oo-offer-chip]')];
    if (!this.slides.length) return;

    this.interval = Number(box.dataset.interval) || 0;
    this.motionAlways = box.hasAttribute('data-motion-always');
    // Nur als oberste Section ist das erste Foto ein LCP-Kandidat (oo-hero.liquid, section.index).
    this.priority = box.hasAttribute('data-priority');
    this.offerIndex = -1;
    this.offerHolds = new Set();
    this.updateOfferState();
    // Startangebot steht im HTML (oo-hero.liquid, data-start); ohne Angabe wie früher zufällig.
    const start = Number.parseInt(box.dataset.start, 10);
    const valid = Number.isInteger(start) && start >= 0 && start < this.slides.length;
    this.showOffer(valid ? start : Math.floor(Math.random() * this.slides.length), true);

    if (this.interval && this.slides.length > 1) {
      this.card?.addEventListener('pointerenter', this.onOfferPointerEnter);
      this.card?.addEventListener('pointerleave', this.onOfferPointerLeave);
      this.card?.addEventListener('focusin', this.onOfferFocusIn);
      this.card?.addEventListener('focusout', this.onOfferFocusOut);
    }
  }

  // is-offer-auto zeigt den Pauseknopf auch bei "weniger Bewegung", sobald Angebote von selbst wechseln (oo-hero.css).
  updateOfferState() {
    this.classList.toggle('is-offer-motion', this.offerMotion());
    this.classList.toggle('is-offer-auto', Boolean(this.interval && this.slides?.length > 1 && this.offerMotion()));
  }

  async showOffer(index, first = false) {
    const next = this.slides[index];
    const prev = this.slides[this.offerIndex];
    if (!next || next === prev) return;
    const ticket = ++this.offerTicket;

    // Die Angebotskarte gibt es auf jeder Breite, auf dem Handy als kleines Vorschaubild (oo-hero.css).
    // Steht der Hero weiter unten, bleibt das erste Foto lazy: Der Browser lädt es selbst, sobald die Folie in Sichtnähe kommt.
    // Bei Wechseln muss eager vor decode() stehen, sonst wartet decode() in einer verborgenen Folie unter Umständen endlos.
    const img = next.querySelector('img');
    if (img && (!first || this.priority)) {
      img.loading = 'eager';
      if (first) img.fetchPriority = 'high';
    }

    // Beim Wechsel erst tauschen, wenn das neue Foto geladen ist, damit keine leere Karte aufblitzt.
    // Höchstens DECODE_WAIT warten: Meldet decode() sich nie (manche Handys), bliebe der Wechsel sonst für immer stehen.
    if (!first && img) {
      let decodeTimer;
      await Promise.race([
        img.decode().catch(() => {}),
        new Promise((resolve) => {
          decodeTimer = setTimeout(resolve, DECODE_WAIT);
        }),
      ]);
      clearTimeout(decodeTimer);
      // Inzwischen pausiert, festgehalten, weggescrollt oder überholt: Wechsel verwerfen.
      // Der Index bleibt beim sichtbaren Angebot, beim Fortsetzen kommt dasselbe Foto wieder dran.
      if (ticket !== this.offerTicket || !this.offerRunning()) return;
    }

    this.offerIndex = index;
    // Das erste Foto kann das LCP-Bild sein: ohne Einflug (architektur.md, „Das LCP-Bild nie animieren“).
    const animate = !first && this.offerMotion();
    clearTimeout(this.leaveTimer);
    this.slides.forEach((slide) => {
      if (slide !== prev && slide !== next) slide.classList.remove('is-active', 'is-leaving', 'is-entering');
    });

    next.classList.add('is-active');
    if (animate) {
      next.classList.add('is-entering');
      requestAnimationFrame(() => requestAnimationFrame(() => next.classList.remove('is-entering')));
    }
    if (prev) {
      prev.classList.remove('is-active');
      if (animate) {
        prev.classList.add('is-leaving');
        // Die alte Karte blendet erst nach 0,4 s für 0,35 s aus (oo-hero.css), also nach 0,75 s weg.
        this.leaveTimer = setTimeout(() => prev.classList.remove('is-leaving'), 800);
      }
    }
    this.chips.forEach((chip, i) => chip.classList.toggle('is-active', i === index));

    this.scheduleOffer();
  }

  // Der Inhaber kann den Wechsel auch bei "weniger Bewegung" im Gerät erlauben (Einstellung der Section).
  offerMotion() {
    return document.documentElement.classList.contains('oo-motion') && (this.motionAlways || !prefersReducedMotion());
  }

  offerRunning() {
    return Boolean(
      this.interval &&
        this.slides?.length > 1 &&
        this.visible &&
        !this.paused &&
        !this.offerHolds?.size &&
        this.offerMotion(),
    );
  }

  scheduleOffer() {
    clearTimeout(this.offerTimer);
    if (!this.offerRunning()) return;
    const nextIndex = (this.offerIndex + 1) % this.slides.length;
    // Das folgende Foto erst vorladen, wenn wirklich ein Wechsel ansteht (sichtbar, nicht angehalten).
    const upcoming = this.slides[nextIndex]?.querySelector('img');
    if (upcoming) upcoming.loading = 'eager';
    this.offerTimer = setTimeout(() => this.showOffer(nextIndex), this.interval);
  }

  // source: 'pointer' oder 'focus'.
  holdOffer(source, held) {
    if (held) this.offerHolds.add(source);
    else this.offerHolds.delete(source);
    this.scheduleOffer();
  }

  setup() {
    this.teardown();
    if (prefersReducedMotion()) return;

    if (this.tilt && this.card && finePointer.matches) {
      this.addEventListener('pointermove', this.onPointerMove);
      this.addEventListener('pointerleave', this.onPointerLeave);
    }

    if (!hasScrollTimeline) {
      this.scrollParallax = true;
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.queueScroll();
    }
  }

  teardown() {
    this.removeEventListener('pointermove', this.onPointerMove);
    this.removeEventListener('pointerleave', this.onPointerLeave);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = 0;
    this.scrollParallax = false;
    this.style.removeProperty('--oo-scroll');
    this.resetTilt();
  }

  queueTilt(event) {
    if (event.pointerType === 'touch') return;
    this.pointerX = event.clientX;
    this.pointerY = event.clientY;
    if (this.tiltFrame) return;
    this.tiltFrame = requestAnimationFrame(() => {
      this.tiltFrame = 0;
      const rect = this.card.getBoundingClientRect();
      const x = clamp((this.pointerX - rect.left) / rect.width - 0.5, -0.5, 0.5);
      const y = clamp((this.pointerY - rect.top) / rect.height - 0.5, -0.5, 0.5);
      this.tilt.style.setProperty('--oo-tilt-x', x.toFixed(3));
      this.tilt.style.setProperty('--oo-tilt-y', y.toFixed(3));
    });
  }

  // Ohne Werte fällt die Figur über die CSS-Transition sanft zurück.
  resetTilt() {
    cancelAnimationFrame(this.tiltFrame);
    this.tiltFrame = 0;
    this.tilt?.style.removeProperty('--oo-tilt-x');
    this.tilt?.style.removeProperty('--oo-tilt-y');
  }

  // 0 = Hero kommt unten ins Bild, 0.5 = mittig, 1 = oben verschwunden.
  queueScroll() {
    if (!this.scrollParallax || !this.visible || this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = 0;
      const rect = this.getBoundingClientRect();
      const viewport = window.innerHeight;
      const progress = clamp((viewport - rect.top) / (viewport + rect.height), 0, 1);
      this.style.setProperty('--oo-scroll', progress.toFixed(3));
    });
  }

  setPaused(paused) {
    this.paused = paused;
    this.classList.toggle('is-snow-paused', paused);
    this.scheduleOffer();
    if (!this.toggle) return;
    this.toggle.toggleAttribute('data-paused', paused);
    if (this.toggleLabel) {
      this.toggleLabel.textContent = paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause;
    }
  }
}

if (!customElements.get('oo-hero')) customElements.define('oo-hero', OoHero);
